import {
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import auth from "core/model/api/endpoints/auth";
import { QueryProgress } from "core/types";
import { usePreventUnload } from "dsl/hooks";
import { debounce } from "lodash";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { emailRegex } from "react-forms-state/src";
import { useParams } from "react-router-dom";

export function useSingleSignOn({
  handleLogin,
}: {
  handleLogin: (
    props: { ssoToken?: string },
    setProgress: Dispatch<SetStateAction<QueryProgress>>,
  ) => Promise<void>;
}) {
  const { token: ssoToken } = useParams();
  const isSsoRedirect = !!ssoToken;
  const [ssoUrl, setSsoUrl] = useState<string>();
  const [progressSso, setProgressSso] = useState<QueryProgress>(
    isSsoRedirect ? QUERY_PROGRESS_PENDING : QUERY_PROGRESS_NOT_STARTED,
  );
  const [progressValidateEmail, setProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  const handleSsoLogin = useCallback(() => {
    if (ssoUrl) {
      setProgressSso(QUERY_PROGRESS_PENDING);
      window.location.href = ssoUrl;
    }
  }, [ssoUrl, setProgressSso]);

  const checkSsoEligibility = useCallback(
    debounce(async ({ email }: { email: string }) => {
      try {
        setProgress(QUERY_PROGRESS_PENDING);

        if (!email || !emailRegex.test(email)) {
          throw new Error("Invalid email");
        }

        const res = await auth.oauthCheck({ email });
        const ssoUrl = res?.url;
        if (ssoUrl) {
          setSsoUrl(ssoUrl);
          setProgress(QUERY_PROGRESS_SUCCEED);
        } else {
          throw new Error("Email not eligible for SSO");
        }
      } catch (_err) {
        setProgress(QUERY_PROGRESS_NOT_STARTED);
        setSsoUrl(undefined);
      }
    }, 300),
    [auth.oauthCheck],
  );

  useEffect(() => {
    if (ssoToken) {
      handleLogin({ ssoToken }, setProgressSso);
    }
  }, [handleLogin, ssoToken]);

  usePreventUnload(progressSso);

  return {
    checkSsoEligibility,
    handleSsoLogin,
    isSsoEligible: !!ssoUrl,
    isSsoRedirect,
    progressSso,
    progressValidateEmail,
    ssoToken,
    ssoUrl,
  };
}
